/**
 * Bridge Game Constants
 * Card definitions, scoring rules, and game configuration
 */

// Card suits and their properties
export const SUITS = {
  SPADES: { name: 'Spades', symbol: '♠', color: '#1a1a2e', rank: 4 },
  HEARTS: { name: 'Hearts', symbol: '♥', color: '#dc2626', rank: 3 },
  DIAMONDS: { name: 'Diamonds', symbol: '♦', color: '#dc2626', rank: 2 },
  CLUBS: { name: 'Clubs', symbol: '♣', color: '#1a1a2e', rank: 1 }
};

export const SUIT_ORDER = ['CLUBS', 'DIAMONDS', 'HEARTS', 'SPADES'];

// Card values (2-14, where 11=J, 12=Q, 13=K, 14=A)
export const CARD_VALUES = {
  2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9', 10: '10',
  11: 'J', 12: 'Q', 13: 'K', 14: 'A'
};

// High card points for bidding
export const HIGH_CARD_POINTS = {
  14: 4, // Ace
  13: 3, // King
  12: 2, // Queen
  11: 1, // Jack
};

// Positions at the table
export const POSITIONS = ['SOUTH', 'WEST', 'NORTH', 'EAST'];
export const PARTNERSHIPS = {
  NS: ['NORTH', 'SOUTH'],
  EW: ['EAST', 'WEST']
};

// Bid levels and strains
export const BID_LEVELS = [1, 2, 3, 4, 5, 6, 7];
export const BID_STRAINS = ['CLUBS', 'DIAMONDS', 'HEARTS', 'SPADES', 'NOTRUMP'];

// Special bids
export const SPECIAL_BIDS = {
  PASS: 'PASS',
  DOUBLE: 'DOUBLE',
  REDOUBLE: 'REDOUBLE'
};

// Scoring
export const TRICK_VALUES = {
  CLUBS: 20,
  DIAMONDS: 20,
  HEARTS: 30,
  SPADES: 30,
  NOTRUMP: { first: 40, subsequent: 30 }
};

export const GAME_BONUS = {
  NOT_VULNERABLE: 300,
  VULNERABLE: 500
};

export const SLAM_BONUS = {
  SMALL: { NOT_VULNERABLE: 500, VULNERABLE: 750 },
  GRAND: { NOT_VULNERABLE: 1000, VULNERABLE: 1500 }
};

export const UNDERTRICK_PENALTIES = {
  NOT_VULNERABLE: { undoubled: 50, doubled: 100, redoubled: 200 },
  VULNERABLE: { undoubled: 100, doubled: 200, redoubled: 400 }
};

// Create a full deck of cards
export function createDeck() {
  const deck = [];
  Object.keys(SUITS).forEach(suit => {
    for (let value = 2; value <= 14; value++) {
      deck.push({
        id: `${suit}_${value}`,
        suit,
        value,
        display: CARD_VALUES[value],
        symbol: SUITS[suit].symbol,
        color: SUITS[suit].color
      });
    }
  });
  return deck;
}

// Shuffle deck using Fisher-Yates
export function shuffleDeck(deck) {
  const shuffled = [...deck];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Deal cards to 4 players (13 each)
export function dealCards() {
  const deck = shuffleDeck(createDeck());
  return {
    SOUTH: sortHand(deck.slice(0, 13)),
    WEST: sortHand(deck.slice(13, 26)),
    NORTH: sortHand(deck.slice(26, 39)),
    EAST: sortHand(deck.slice(39, 52))
  };
}

// Sort hand by suit then value
export function sortHand(cards) {
  return [...cards].sort((a, b) => {
    const suitDiff = SUITS[b.suit].rank - SUITS[a.suit].rank;
    if (suitDiff !== 0) return suitDiff;
    return b.value - a.value;
  });
}

// Calculate high card points for a hand
export function calculateHCP(hand) {
  return hand.reduce((total, card) => {
    return total + (HIGH_CARD_POINTS[card.value] || 0);
  }, 0);
}

// Calculate distribution points
export function calculateDistributionPoints(hand) {
  const suitCounts = { SPADES: 0, HEARTS: 0, DIAMONDS: 0, CLUBS: 0 };
  hand.forEach(card => suitCounts[card.suit]++);
  
  let points = 0;
  Object.values(suitCounts).forEach(count => {
    if (count === 0) points += 3; // Void
    else if (count === 1) points += 2; // Singleton
    else if (count === 2) points += 1; // Doubleton
  });
  return points;
}

// Get cards of a specific suit from hand
export function getCardsOfSuit(hand, suit) {
  return hand.filter(card => card.suit === suit);
}

// Check if a bid is valid given the current auction
export function isValidBid(bid, currentContract, lastBid, passCount) {
  if (bid === SPECIAL_BIDS.PASS) return true;
  
  if (bid === SPECIAL_BIDS.DOUBLE) {
    // Can only double opponent's bid
    return currentContract && !currentContract.doubled;
  }
  
  if (bid === SPECIAL_BIDS.REDOUBLE) {
    // Can only redouble own side's doubled contract
    return currentContract && currentContract.doubled && !currentContract.redoubled;
  }
  
  // Regular bid must be higher than current contract
  if (!currentContract) return true;
  
  const bidLevel = bid.level;
  const bidStrain = BID_STRAINS.indexOf(bid.strain);
  const contractLevel = currentContract.level;
  const contractStrain = BID_STRAINS.indexOf(currentContract.strain);
  
  if (bidLevel > contractLevel) return true;
  if (bidLevel === contractLevel && bidStrain > contractStrain) return true;
  
  return false;
}

// Determine winner of a trick
export function determineTrickWinner(trick, trumpSuit) {
  let winningCard = trick[0];
  let winnerIndex = 0;
  const leadSuit = trick[0].card.suit;
  
  for (let i = 1; i < trick.length; i++) {
    const card = trick[i].card;
    const winning = winningCard.card;
    
    // Trump beats non-trump
    if (trumpSuit) {
      if (card.suit === trumpSuit && winning.suit !== trumpSuit) {
        winningCard = trick[i];
        winnerIndex = i;
        continue;
      }
      if (winning.suit === trumpSuit && card.suit !== trumpSuit) {
        continue;
      }
    }
    
    // Must follow suit - only same suit can win
    if (card.suit === leadSuit && winning.suit !== leadSuit) {
      winningCard = trick[i];
      winnerIndex = i;
      continue;
    }
    
    // Higher card of same suit wins
    if (card.suit === winning.suit && card.value > winning.value) {
      winningCard = trick[i];
      winnerIndex = i;
    }
  }
  
  return { winner: winningCard.player, winnerIndex };
}

// Calculate score for a contract
export function calculateScore(contract, tricksTaken, vulnerable) {
  const tricksNeeded = 6 + contract.level;
  const tricksMade = tricksTaken;
  const overUnder = tricksMade - tricksNeeded;
  
  if (overUnder >= 0) {
    // Made contract
    let score = 0;
    
    // Trick score
    if (contract.strain === 'NOTRUMP') {
      score = TRICK_VALUES.NOTRUMP.first + (contract.level - 1) * TRICK_VALUES.NOTRUMP.subsequent;
    } else {
      score = contract.level * TRICK_VALUES[contract.strain];
    }
    
    // Double/redouble multiplier for trick score
    if (contract.doubled) score *= 2;
    if (contract.redoubled) score *= 4;
    
    // Game bonus (100+ trick points)
    if (score >= 100) {
      score += vulnerable ? GAME_BONUS.VULNERABLE : GAME_BONUS.NOT_VULNERABLE;
    } else {
      score += 50; // Part score bonus
    }
    
    // Slam bonuses
    if (contract.level === 6) {
      score += vulnerable ? SLAM_BONUS.SMALL.VULNERABLE : SLAM_BONUS.SMALL.NOT_VULNERABLE;
    } else if (contract.level === 7) {
      score += vulnerable ? SLAM_BONUS.GRAND.VULNERABLE : SLAM_BONUS.GRAND.NOT_VULNERABLE;
    }
    
    // Overtrick bonuses
    if (overUnder > 0) {
      if (contract.doubled) {
        score += overUnder * (vulnerable ? 200 : 100);
      } else if (contract.redoubled) {
        score += overUnder * (vulnerable ? 400 : 200);
      } else {
        const overtrickValue = contract.strain === 'NOTRUMP' ? 30 : TRICK_VALUES[contract.strain];
        score += overUnder * overtrickValue;
      }
    }
    
    // Insult bonus for making doubled/redoubled
    if (contract.doubled) score += 50;
    if (contract.redoubled) score += 100;
    
    return score;
  } else {
    // Failed contract - negative score (penalty)
    const undertricks = Math.abs(overUnder);
    const penalties = vulnerable ? UNDERTRICK_PENALTIES.VULNERABLE : UNDERTRICK_PENALTIES.NOT_VULNERABLE;
    
    if (contract.redoubled) {
      return -(undertricks * penalties.redoubled);
    } else if (contract.doubled) {
      return -(undertricks * penalties.doubled);
    } else {
      return -(undertricks * penalties.undoubled);
    }
  }
}

export default {
  SUITS,
  SUIT_ORDER,
  CARD_VALUES,
  POSITIONS,
  PARTNERSHIPS,
  BID_LEVELS,
  BID_STRAINS,
  SPECIAL_BIDS,
  createDeck,
  shuffleDeck,
  dealCards,
  sortHand,
  calculateHCP,
  calculateDistributionPoints,
  getCardsOfSuit,
  isValidBid,
  determineTrickWinner,
  calculateScore
};
